<template>
  <div class="w-full">
    <LcButton
      :aria-haspopup="playSlipValid ? 'false' : 'dialog'"
      class="button w-full"
      icon="arrow-right"
      icon-position="right"
      no-padding
      size="large"
      @click="redirectToCheckout"
    >
      {{ $t('common.words.checkout') }}
    </LcButton>

    <Modal
      cancel-variant="primary"
      confirm-variant="primary-ghost"
      data-testid="high-frequency-play-slip-navbar-incomplete-plays-modal"
      :is-visible="isInCompletePlaysModalVisible"
      :tracking="{
        action: 'checkout',
        topicKey: 'incomplete-plays-confirmation',
      }"
      @cancel="fillIncompletePlaysWithQuickPick"
      @confirm="closeIncompletePlaysModal"
    >
      <template #headline>
        {{ $t('lottery.playslipnavbar.modal.incompleteplays.headline', 1) }}
      </template>

      <template #cancel>
        {{ $t('lottery.playslipnavbar.modal.incompleteplays.cancel') }}
      </template>

      <template #confirm>
        {{ $t('lottery.playslipnavbar.modal.incompleteplays.confirm') }}
      </template>

      {{ $t('lottery.playslipnavbar.modal.incompleteplays.message') }}
    </Modal>

    <AccountVerificationModalControl
      :is-visible="showIsNotVerifiedModal"
      :redirect="
        localePath(`/checkout?p=${gameId.toLowerCase()}&backroute=/keno/${gameId.toLowerCase()}`)
      "
      @close="handleAccountVerificationClose"
    />

    <CheckoutRestrictions
      v-if="storeMutationError"
      v-bind="{ ...storeMutationError }"
      close-all-modals-on-confirm
    />

    <HighFrequencyGameMaxPriceModal
      v-if="maxPlaySlipPriceError"
      :limit="maxPlaySlipPriceError"
      @confirm="closeMaxPriceModal"
    />
  </div>
</template>

<script lang="ts" setup>
import { KnownProductType, Status } from '#api/shared/@types/generated/backend/graphql-schema-types'

type Emits = {
  (e: 'use-quickpick'): void
}

const emit = defineEmits<Emits>()

const { navigateToLocalized } = useRouterUtils()
const localePath = useLocalePath()
const {
  config,
  currentPrice,
  disclaimerAcceptedConfig,
  disclaimerAcceptedCookie,
  disclaimerAcceptedError,
  gameId,
  isQuickPick,
  maxPlaySlipPriceError,
  playSlipValid,
  selectedAddOns,
  selectedQuantity,
  selectedWager,
  storeHighFrequencyGameSlip,
  storeMutationError,
} = useHighFrequencyGame()
const route = useRoute()
const { isLoggedIn } = useAuth()
const { pushAddToCartEvent, pushBeginCheckoutEvent } = useTracking()
const { createECommerceEventData } = useTrackingData()
const { logTrackingError } = useTrackingErrorLog()

const isInCompletePlaysModalVisible = ref(false)
const showIsNotVerifiedModal = ref(false)

const closeIncompletePlaysModal = () => {
  isInCompletePlaysModalVisible.value = false
}

const fillIncompletePlaysWithQuickPick = () => {
  closeIncompletePlaysModal()
  emit('use-quickpick')
}

const closeMaxPriceModal = () => {
  maxPlaySlipPriceError.value = undefined
}

const wagers = computed(() => config.value?.wagers || [])

const generateEventData = async () => {
  if (!config.value) return null
  return await createECommerceEventData({
    addOns: selectedAddOns.value,
    currentPrice: currentPrice.value,
    gameId: config.value.gameId,
    isQuickPick: isQuickPick.value,
    numberOfGames: selectedQuantity.value ?? 0,
    type: KnownProductType.HighFrequencyGame,
    wager:
      wagers.value.find((wager) => wager.name === selectedWager.value)?.price.amount.value ?? 0,
  })
}

const redirectToCheckout = async () => {
  if (!playSlipValid.value) {
    isInCompletePlaysModalVisible.value = true
    return
  }

  if (!disclaimerAcceptedConfig.value && !disclaimerAcceptedCookie.value?.value) {
    disclaimerAcceptedError.value = true
    return
  }

  if (!isLoggedIn.value) {
    return await navigateToLocalized(
      createRedirectLocation(
        {
          path: '/login',
        },
        encodeRedirectPathWithQuery('/checkout/redirect', {
          p: gameId.toLowerCase(),
          // backroute needs to be last to work properly
          backroute: route.path,
        }),
      ),
      { replace: true },
    )
  }

  if (isLoggedIn.value) {
    const player = await useUserDataLazyQuery().load()
    if (player && player.player.status !== Status.Verified) {
      showIsNotVerifiedModal.value = true
      return
    }

    const storedSuccessful = await storeHighFrequencyGameSlip()

    if (storedSuccessful === false) return
  }

  try {
    pushAddToCartEvent(await generateEventData())
    pushAddToCartEvent(await generateEventData(), true)
  } catch (err) {
    logTrackingError('add_to_cart_by_next_step', err)
  }

  try {
    pushBeginCheckoutEvent(await generateEventData())
  } catch (err) {
    logTrackingError('begin_checkout', err)
  }

  await navigateToLocalized({
    path: '/checkout',
    query: {
      p: gameId.toLowerCase(),
    },
  })
}

const handleAccountVerificationClose = () => {
  showIsNotVerifiedModal.value = false
}

defineExpose({
  redirectToCheckout,
})
</script>
